<script>
  import { createEventDispatcher } from 'svelte';
  import { Checkbox, InlineNotification, Modal, TextArea, TextInput } from 'carbon-components-svelte';

  import RestoreTable from './RestoreTable.svelte';
  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';

  import { getServerErrorMessage } from '../../../utils';
  import { requestRestore } from '../../../services';

  const dispatch = createEventDispatcher();
  const tableHeaders = [
    { key: 'path', value: 'Path' },
    { key: 'from', value: 'Start Date' },
    { key: 'to', value: 'End Date' },
    { key: 'lifetime', value: 'Lifetime (days)' },
    { key: 'restoring', value: 'Restoring' },
  ];

  export let open = false,
    paths;

  let resultNotification,
    loading = false,
    understand = false,
    form = { ticketId: null, note: null },
    validationsErrors = { ticketId: null };

  async function onFormSubmit() {
    if (!form.ticketId.trim()) {
      validationsErrors = { ticketId: 'Ticket is required' };
      return;
    }

    validationsErrors = { ticketId: null };
    resultNotification = null;
    loading = true;

    try {
      const response = await requestRestore({
        ...form,
        paths,
      });

      if (!response.id) {
        throw new Error('Invalid response from server');
      }

      dispatch('success', { id: response.id });
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.details?.[0]?.context?.details?.[1]?.message ??
        getServerErrorMessage(error) ??
        'Verify your submission and try again!';
      console.error('[RequestModal] Failed to request restore!', errorMessage);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to request restore! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  function handleClose() {
    if (loading) {
      return;
    }

    dispatch('close');
  }

  function handleCustomChangeEvent(key) {
    return function onCustomChange({ detail: value }) {
      form = { ...form, [key]: value };
    };
  }

  function handleChangeEvent(key) {
    return function onChange({ target: { value } }) {
      form = { ...form, [key]: value };
    };
  }

  function stopPropagation(event) {
    event.stopPropagation();
  }

  function onCheck({ detail: checked }) {
    understand = checked;
  }
</script>

<Modal
  bind:open
  modalHeading="Request S3 Object Restore"
  primaryButtonText="Request"
  secondaryButtonText="Cancel"
  preventCloseOnClickOutside
  size="lg"
  shouldSubmitOnEnter={false}
  primaryButtonDisabled={loading || !understand}
  on:click:button--secondary={handleClose}
  on:close={handleClose}
  on:submit={onFormSubmit}
>
  <LoadingSpinner {loading} small>
    {#if resultNotification}
      <InlineNotification
        kind={resultNotification.kind}
        lowContrast
        title={resultNotification.title}
        subtitle={resultNotification.subtitle}
        hideCloseButton
      />
    {/if}
    <p>The following paths are about to be requested for restore. Please provide related ticket for reference.</p>
    <br />
    <TextInput
      name="ticketId"
      autocomplete="off"
      invalid={!!validationsErrors.ticketId}
      invalidText={validationsErrors.ticketId}
      labelText="Ticket"
      placeholder="Related ticket..."
      value={form.ticketId}
      required
      on:change={handleCustomChangeEvent('ticketId')}
    />
    <RestoreTable {paths} {tableHeaders} />
    <br />
    <TextArea
      name="note"
      labelText="Note"
      placeholder="Enter note for approver..."
      value={form.note}
      required
      on:change={handleChangeEvent('note')}
    />
    <div class="understand">
      <Checkbox
        labelText="I have reviewed the paths and provided a valid ticket."
        disabled={!form.ticketId}
        checked={understand}
        on:click={stopPropagation}
        on:check={onCheck}
      />
    </div>
  </LoadingSpinner>
</Modal>

<style>
  .understand {
    margin-top: 2rem;
  }
</style>
